import {useDataModelo3dContext, useDataModelo3dToggleContext} from "../../../DataProvider.jsx";
import InputSpinner2 from "./InputSpinner2/InputSpinner2.jsx";
import PropTypes from "prop-types";
import {Download, X} from "lucide-react";
import {Checkbox} from "../../../../../components/ui/index.js";
import ParamPanel from "./ParamPanel/ParamPanel.jsx";
import {getProduct, patchPath, schemaFields} from "../../../../../products/registry.js";

const formatPocSeconds = (seconds) => {
    const value = Number(seconds)
    if (!Number.isFinite(value)) return "-"
    return `${value.toFixed(value < 1 ? 3 : 2)}s`
}

/**
 * Radio y calidad de un canto, con el mismo fallback que aplica el backend.
 *
 * Un diseño guardado antes de separar los radios solo tiene `radius_rounded`
 * más los flags `round_top`/`round_bottom`; hasta que el usuario toque el
 * panel no existe la clave por canto, así que se deriva de lo antiguo en vez
 * de mostrar un 0 que no es lo que tiene guardado.
 */
function readEdgeRounding(baseLayout = {}, edge) {
    const radius = baseLayout[`radius_rounded_${edge}`]
    const segments = baseLayout[`round_segments_${edge}`]
    const legacyEnabled = baseLayout[`round_${edge}`] !== false
    return {
        radius: radius ?? (legacyEnabled ? baseLayout.radius_rounded ?? 0 : 0),
        segments: segments ?? baseLayout.round_segments ?? 8,
    }
}

/** ¿Pinta algo este campo de la caja con lo que hay elegido ahora? */
function isHolderFieldInUse(key, holder = {}) {
    const grip = holder.grip ?? 'none'
    const wallPattern = holder.wallPattern ?? 'none'
    const floorPattern = holder.floorPattern ?? 'none'
    if (key.startsWith('grip_')) return grip !== 'none'
    if (key === 'color') return Boolean(holder.customColor)
    if (key === 'pattern_margin_mm') return wallPattern !== 'none'
    if (key === 'pattern_seed') return wallPattern === 'voronoi' || floorPattern === 'voronoi'
    if (key.startsWith('pattern_')) return wallPattern !== 'none' || floorPattern !== 'none'
    return true
}

const ThiknessAndMoreEditor = () => {
    const dataModelo3d = useDataModelo3dContext()
    const setDataModelo3d = useDataModelo3dToggleContext()
    const noBackground = Boolean(dataModelo3d.baseLayout?.noBackground)
    const holderSchema = getProduct('keyring').holderSchema
    const holderEnabled = Boolean(dataModelo3d.coasterHolder?.enabled)
    const corkSchema = getProduct('keyring').corkSchema
    const corkEnabled = Boolean(dataModelo3d.cork?.enabled)
    // Apagada solo se ve el interruptor, y cada cota se esconde mientras no
    // haga nada (sin muescas, sus medidas; sin calado, las del patrón).
    const holderHidden = schemaFields(holderSchema)
        .map(field => field.key)
        .filter(key => !holderEnabled ? key !== 'enabled' : !isHolderFieldInUse(key, dataModelo3d.coasterHolder))

    const roundToDecimal = (value, decimals) => {
        const factor = Math.pow(10, decimals);
        return Math.round(value * factor) / factor;
    };

    const handleThikness = (newThikness) => {
        setDataModelo3d({
            thiknessModelo3d: roundToDecimal(newThikness, 1),
        })
    };

    const handleDrawingDepth = (newDepth) => {
        setDataModelo3d({
            drawingDepthMm: roundToDecimal(newDepth, 2),
        })
    };

    const handleRoundSegments = (edge) => (newSegments) => {
        updateRounding({
            [`round_segments_${edge}`]: Math.min(100, Math.max(4, Math.round(newSegments))),
        })
    };

    const handleRadius = (edge) => (radius) => {
        updateRounding({ [`radius_rounded_${edge}`]: Math.max(0, roundToDecimal(radius, 2)) })
    };

    const updateRounding = (update) => {
        setDataModelo3d({
            baseLayout: {
                ...dataModelo3d.baseLayout,
                ...update,
            },
        })
    };

    return (
        <div className="flex flex-col gap-5 p-3">
            <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-base-content/75">
                Ajusta los valores y pulsa <strong>Actualizar modelo 3D</strong> en la barra lateral izquierda para aplicarlos y regenerar la vista.
            </div>

            <section className="flex flex-col gap-2">
                <Checkbox
                    label="Sin fondo: extruir solo los dibujos"
                    checked={noBackground}
                    onChange={(enabled) => updateRounding({ noBackground: enabled })}
                />
                <p className="text-sm text-base-content/70">
                    Los dibujos conservan sus colores y huecos, con el grosor total indicado.
                    El fondo se conserva en 2D y se omite en 3D. Las formas separadas quedan como piezas sueltas.
                </p>
                {noBackground && <p className="text-xs text-base-content/60">
                    Los vaciados y agujeros son pasantes. No se aplican profundidad de integración,
                    salientes, redondeo de la base ni alojamientos internos.
                </p>}
            </section>

            <section className="flex flex-col gap-2">
                <h3 className="font-semibold">Grosor total</h3>
                <InputSpinner2
                    label="Milímetros"
                    value={dataModelo3d.thiknessModelo3d}
                    onChange={handleThikness}
                    min={0}
                    max={1000}
                    initialStep={1}
                />
            </section>

            {!noBackground && <section className="flex flex-col gap-2">
                <h3 className="font-semibold">Profundidad de integración</h3>
                <p className="text-sm text-base-content/70">
                    Indica cuánto entra el dibujo dentro del fondo. La cara superior permanece al ras y la cavidad se rellena por completo.
                </p>
                <InputSpinner2
                    label="Milímetros"
                    value={dataModelo3d.drawingDepthMm ?? 0.75}
                    onChange={handleDrawingDepth}
                    min={0.2}
                    max={2}
                    initialStep={0.1}
                />
                <div className="rounded-lg border border-info/30 bg-info/10 p-3 text-xs leading-relaxed text-base-content/75">
                    Orientación: <strong>0,3-0,5 mm</strong> para detalle fino, <strong>0,6-0,8 mm</strong> para uso general y <strong>0,9-1,2 mm</strong> para formas grandes o robustas.
                </div>
            </section>}

            {!noBackground && <section className="flex flex-col gap-2">
                <Checkbox
                    label="Dejar que los dibujos se salgan del fondo"
                    checked={Boolean(dataModelo3d.baseLayout?.drawingsOverflow)}
                    onChange={(enabled) => updateRounding({ drawingsOverflow: enabled })}
                />
                <p className="text-sm text-base-content/70">
                    Para el nombre que cruza la inicial: el dibujo que se sale del fondo no se recorta.
                    Sale entero como pieza aparte, con su profundidad y su saliente, y se pega
                    en el hueco que deja en el fondo.
                </p>
                {dataModelo3d.baseLayout?.drawingsOverflow && <p className="text-xs text-base-content/60">
                    En el 3MF va tumbado junto al llavero para imprimirlo por separado. Los dibujos
                    que caen enteros dentro del fondo se quedan incrustados como siempre.
                </p>}
            </section>}

            {!noBackground && <section className="flex flex-col gap-2">
                <label className="label cursor-pointer px-0">
                    <span className="label-text font-semibold">Redondear bordes</span>
                    <input
                        type="checkbox"
                        className="checkbox checboxMod"
                        checked={dataModelo3d.baseLayout.rounded}
                        onChange={(event) => updateRounding({ rounded: event.target.checked })}
                    />
                </label>

                {dataModelo3d.baseLayout.rounded && (
                    <div className="flex flex-col gap-4">
                        <p className="text-sm text-base-content/70">
                            Cada canto tiene su propio radio y su propia calidad. Un radio de <strong>0</strong> deja ese canto vivo.
                        </p>

                        {[
                            { edge: 'top', title: 'Canto superior' },
                            { edge: 'bottom', title: 'Canto inferior' },
                        ].map(({ edge, title }) => {
                            const { radius, segments } = readEdgeRounding(dataModelo3d.baseLayout, edge)
                            return (
                                <div key={edge} className="flex flex-col gap-2 rounded-lg border border-base-300 p-3">
                                    <h4 className="text-sm font-semibold">{title}</h4>
                                    <InputSpinner2
                                        label="Radio en milímetros"
                                        value={radius}
                                        onChange={handleRadius(edge)}
                                        min={0}
                                        max={1000}
                                        initialStep={1}
                                        interval={100}
                                    />
                                    <InputSpinner2
                                        label="Calidad redondeo Manifold"
                                        value={segments}
                                        onChange={handleRoundSegments(edge)}
                                        min={4}
                                        max={100}
                                        initialStep={1}
                                        integer
                                        interval={100}
                                    />
                                </div>
                            )
                        })}

                        <p className="text-xs text-base-content/60">
                            Los dos radios juntos no pueden superar el grosor total: si te pasas, se reducen a la vez manteniendo la proporción.
                        </p>
                    </div>
                )}
            </section>}

            {!noBackground && <section className="flex flex-col gap-2">
                <ParamPanel
                    product={{
                        paramSchema: corkSchema,
                        hiddenSchemaFields: corkEnabled
                            ? []
                            : schemaFields(corkSchema).map(field => field.key).filter(key => key !== 'enabled'),
                    }}
                    values={dataModelo3d}
                    onChange={(path, value) => setDataModelo3d(patchPath(dataModelo3d, path, value))}
                />
            </section>}

            {!noBackground && <section className="flex flex-col gap-2">
                <p className="text-sm text-base-content/70">
                    Para posavasos: una caja abierta que sigue el contorno exterior, con la pila dentro.
                </p>
                <ParamPanel
                    product={{
                        paramSchema: holderSchema,
                        hiddenSchemaFields: holderHidden,
                    }}
                    values={dataModelo3d}
                    onChange={(path, value) => setDataModelo3d(patchPath(dataModelo3d, path, value))}
                />
            </section>}

        </div>
    );
};

// Exported so the schema-driven ParamPanel can show the same diagnostics
// without duplicating this table. Its own hand-written panel above stays until
// the keyring is ported to a schema too.
export function ManifoldPocResultPanel({ state = {}, onClose }) {
    const result = state.result
    const hasResult = Boolean(result || state.error)
    const visibleParts = (result?.parts || []).filter(part => part.vertices > 0 || part.triangles > 0)

    if (!hasResult) return null

    return (
        <div className="customiz3r-manifold-poc__panel mt-3">
            <div className="customiz3r-manifold-poc__header">
                <div>
                    <p className="customiz3r-manifold-poc__eyebrow">Diagnóstico</p>
                    <h3>Resultado ManifoldCAD</h3>
                </div>
                {onClose && (
                    <button type="button" className="btn btn-xs btn-circle btn-ghost" onClick={onClose} title="Cerrar">
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {state.error && (
                <p className="customiz3r-manifold-poc__error">{state.error}</p>
            )}

            {result && (
                <>
                    <div className={`customiz3r-manifold-poc__status ${result.ok ? 'is-ok' : 'is-error'}`}>
                        {result.ok ? 'Resultado generado' : 'No se pudo generar'}
                    </div>

                    <div className="customiz3r-manifold-poc__metrics">
                        <span>Total {formatPocSeconds(result.timings?.total_s)}</span>
                        <span>SVG {formatPocSeconds(result.timings?.extract_svg_s)}</span>
                        <span>Booleanas {formatPocSeconds(result.timings?.manifold_boolean_extrude_s)}</span>
                    </div>

                    <div className="customiz3r-manifold-poc__metrics">
                        <span>Grosor {formatMm(result.parameters?.effective_thickness_mm)}</span>
                        <span>Base {formatMm(result.parameters?.base_slice_height_mm)}</span>
                        <span>Capa sup. {formatMm(result.parameters?.top_slice_height_mm)}</span>
                    </div>

                    <div className="customiz3r-manifold-poc__metrics">
                        <span>Ancho {formatMm(result.parameters?.target_width_mm)}</span>
                        <span>Alto {formatMm(result.parameters?.target_height_mm)}</span>
                        <span>Escala XY {formatScale(result.parameters?.xy_scale)}</span>
                    </div>

                    <div className="customiz3r-manifold-poc__metrics">
                        <span>Agujero</span>
                        <span className="col-span-2">
                            {result.parameters?.hole_perforates_drawings ? 'Fondo y dibujos' : 'Solo fondo'}
                        </span>
                    </div>

                    <div className="customiz3r-manifold-poc__metrics">
                        <span>Radio sup. {formatMm(result.parameters?.effective_radius_top_mm)}</span>
                        <span>Radio inf. {formatMm(result.parameters?.effective_radius_bottom_mm)}</span>
                        <span>
                            Lonchas {result.parameters?.effective_round_segments_top ?? '-'}
                            {' / '}
                            {result.parameters?.effective_round_segments_bottom ?? '-'}
                        </span>
                    </div>

                    {result.parameters?.carved_colors?.length > 0 && (
                        <div className="customiz3r-manifold-poc__metrics">
                            <span>Bajo relieve</span>
                            <span className="col-span-2">
                                {result.parameters.carved_colors.length} color(es) vaciado(s) a {formatMm(result.parameters.carved_depth_mm)}
                            </span>
                        </div>
                    )}

                    {visibleParts.length > 0 && (
                        <div className="customiz3r-manifold-poc__parts">
                            {visibleParts.slice(0, 4).map(part => (
                                <div key={part.key} className="customiz3r-manifold-poc__part">
                                    <span className="customiz3r-manifold-poc__swatch" style={{ background: part.color }} />
                                    <span>{part.key}</span>
                                    <small>{part.vertices}v / {part.triangles}t</small>
                                </div>
                            ))}
                        </div>
                    )}

                    {(result.errors?.length > 0 || result.warnings?.length > 0) && (
                        <div className="customiz3r-manifold-poc__messages">
                            {[...(result.errors || []), ...(result.warnings || [])].slice(0, 3).map((message, index) => (
                                <p key={`${index}-${message}`}>{message}</p>
                            ))}
                        </div>
                    )}

                    <div className="customiz3r-manifold-poc__links">
                        {result.glb_url && (
                            <a className="btn btn-xs btn-outline" href={result.glb_url} target="_blank" rel="noreferrer">
                                <Download className="h-3 w-3" />
                                GLB
                            </a>
                        )}
                        {result.diagnostics_url && (
                            <a className="btn btn-xs btn-outline" href={result.diagnostics_url} target="_blank" rel="noreferrer">
                                JSON
                            </a>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

function formatMm(value) {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return "-"
    return `${numeric.toFixed(2)} mm`
}

function formatScale(value) {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return "-"
    return numeric.toFixed(3)
}

ManifoldPocResultPanel.propTypes = {
    state: PropTypes.shape({
        loading: PropTypes.bool,
        result: PropTypes.object,
        error: PropTypes.string,
    }),
    onClose: PropTypes.func,
}

export default ThiknessAndMoreEditor;
